import { Component, OnInit, Input, EventEmitter, Output, ViewChild, OnChanges, signal, NO_ERRORS_SCHEMA, computed } from '@angular/core';
import { FlowObjectDefinition, FlowObjectType, FormSchema } from '../../../../models/flow-object.model';
import { ConditionalRecipient, ConfigSchema, ConfigSchemaSectionAccessLevel, ConfigSchemaSectionEDocsLocation } from '../../../../models/config-schema.model';
import { FlowDefinition, FlowTarget } from '../../../../models/flow.model';
import { IBaseOption } from '../../../../models/base.model';
import { Utils } from '../../../../shared/utils';
import { AccessLevelComponent } from '../_edocs-fields/access-level/access-level.component';
import { EDocsLocationComponent } from '../_edocs-fields/edocs-location/edocs-location.component';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../../../shared/enums';
import { FlowObjectDefinitionService } from '../../../../services/flow-object-definition.service';
import { IMPORTS } from './_imports';

@Component({
    selector: 'fod-forward',
    templateUrl: './fod-forward.component.html',
    styleUrl: './fod-forward.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})
export class FlowObjectDetailsForwardComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('accessLevelRef') accessLevelRef: AccessLevelComponent;
    @ViewChild('edocsLocationRef') edocsLocationRef: EDocsLocationComponent;
    @ViewChild('edocsLocationConditionalRef') edocsLocationConditionalRef: EDocsLocationComponent;
    // #endregion

    // #region [Type props]
    FlowObjectType = FlowObjectType;
    RecipientMode = Enums.RecipientMode;
    Utils = Utils;
    // #endregion

    // #region [props]
    formFlowObjectSchemaFields: any[] = [];
    // #endregion

    // #region [signals]
    $configSchema = signal(null as ConfigSchema);
    $flowDefinition = signal(null as FlowDefinition);
    $flowObjectDefinition = signal(null as FlowObjectDefinition);
    $isReadOnlyMode = signal(false);
    $recipientMode = signal(Enums.RecipientMode.Fixed);
    $configSchemaSectionAccessLevel = signal(new ConfigSchemaSectionAccessLevel());
    $configSchemaSectionEDocsLocation = signal(new ConfigSchemaSectionEDocsLocation());
    $configSchemaSectionEDocsLocationConditional = signal(new ConfigSchemaSectionEDocsLocation());
    $formFieldConditionalOptions = signal([] as IBaseOption[]);
    $formFieldConditional = signal(null as string);
    $valueConditional = signal(null as string);
    $isOperationEqualConditional = signal(true);
    // #endregion

    // #region [computeds]
    $shouldShowOwnRole = computed(() => this.$flowDefinition().targetId != FlowTarget.Citizen);
    $shouldShowOwnerHeadOfOrganization = computed(() => this.$flowDefinition().targetId != FlowTarget.Citizen);
    $shouldShowOwnerImmediateSupervisor = computed(() =>
        this.$flowDefinition().flowObjectDefinitions.filter(x => x.typeId == FlowObjectType.TaskForward).sort((a, b) => a.outerIndex - b.outerIndex)[0].outerIndex
        < this.$flowObjectDefinition().outerIndex
    );
    // #endregion

    // #region [Input/Output]
    @Input() _configSchema: ConfigSchema;
    @Input() _flowDefinition: FlowDefinition;
    @Input() _flowObjectDefinition: FlowObjectDefinition;
    @Input() _isReadOnlyMode: boolean;
    @Output() _configSchemaChangeEvent = new EventEmitter<ConfigSchema>();
    @Output() _recipientModeChange = new EventEmitter<Enums.RecipientMode>();
    // #endregion

    constructor(
        private toastr: ToastrService,
        private flowObjectDefinitionService: FlowObjectDefinitionService
    ) { }

    async ngOnInit() {
        let formFlowObject = this.$flowDefinition().flowObjectDefinitions.find(x => x.typeId == FlowObjectType.StartForm);
        if (formFlowObject != null) {
            if (Utils.isNullOrEmpty(formFlowObject.formSchema)) {
                const response = await this.flowObjectDefinitionService.getFormSchema(formFlowObject.id);

                if (!response.isSuccess) {
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                formFlowObject.formSchema = response.data;
            }

            let formSchema = JSON.parse(formFlowObject.formSchema) as FormSchema;
            this.formFlowObjectSchemaFields = Utils.getFormFieldsWithUsableValue(formSchema.components);
        }

        for (let prop in this.$configSchemaSectionAccessLevel()) {
            Utils.updSignal(this.$configSchemaSectionAccessLevel, x => {
                x[prop] = this.$configSchema().taskForward.accessLevel[prop] || x[prop];
                return x;
            });
        }

        for (let prop in this.$configSchemaSectionEDocsLocation()) {
            Utils.updSignal(this.$configSchemaSectionEDocsLocation, x => {
                x[prop] = this.$configSchema().taskForward.recipient[prop] || x[prop];
                return x;
            });
        }

        this.initForwardInfo();
    }

    ngOnChanges() {
        this.$configSchema.set(this._configSchema);
        this.$flowDefinition.set(this._flowDefinition);
        this.$flowObjectDefinition.set(this._flowObjectDefinition);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
    }

    clearDestinatario() {
        this.$configSchemaSectionEDocsLocation.set(new ConfigSchemaSectionEDocsLocation());

        Utils.updSignal(this.$configSchema, x => {
            Object.assign(x.taskForward.recipient, this.$configSchemaSectionEDocsLocation());
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    removeCondition(condition: ConditionalRecipient) {
        if (this.$isReadOnlyMode()) return;

        Utils.updSignal(this.$configSchema, x => {
            x.taskForward.conditionalRecipients = x.taskForward.conditionalRecipients.filter(x => x.timestamp != condition.timestamp);
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    moveCondition(condition: ConditionalRecipient, isDirectionUp = false) {
        if (this.$isReadOnlyMode()) return;

        let offset = isDirectionUp ? -1 : 1;

        let fromKey = condition.timestamp;
        let fromCondition = this.$configSchema().taskForward.conditionalRecipients.find(x => x.timestamp == fromKey);
        let toCondition = this.$configSchema().taskForward.conditionalRecipients[this.$configSchema().taskForward.conditionalRecipients.indexOf(fromCondition) + offset];
        let toKey = toCondition.timestamp;

        fromCondition.timestamp = toKey;
        toCondition.timestamp = fromKey;

        Utils.updSignal(this.$configSchema, x => {
            x.taskForward.conditionalRecipients = x.taskForward.conditionalRecipients.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    removeAllConditions() {
        if (this.$isReadOnlyMode()) return;

        Utils.updSignal(this.$configSchema, x => {
            x.taskForward.conditionalRecipients = [];
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    prettyPrint(condition: ConditionalRecipient): string {
        var optionDescription = this.$formFieldConditionalOptions().find(x => x.value == condition.formFieldId)?.description;
        optionDescription = optionDescription || Enums.Messages.DefaultEmptyValue;

        return '<span>Se o valor de {0} for {1} {2}, encaminhar para {3}</span>'
            .replace('{0}', `<b style="color:red">${optionDescription}</b>`)
            .replace('{1}', `<b style="color:purple">${condition.isOperationEqual ? 'igual a' : 'diferente de'}</b>`)
            .replace('{2}', `<b style="color:blue">"${condition.value}"</b>`)
            .replace('{3}', `<b style="color:green">${condition.recipient?.name}</b>`);
    }

    recipientModeChange(event?) {
        if (event != null) {
            this.$recipientMode.set(event);
        }

        let toImmediateSupervisor = false;
        if (this.$recipientMode() == Enums.RecipientMode.ImmediateSupervisor) {
            toImmediateSupervisor = true;
        }

        let toOwnerImmediateSupervisor = false;
        if (this.$recipientMode() == Enums.RecipientMode.OwnerImmediateSupervisor) {
            toOwnerImmediateSupervisor = true;
        }

        let toOwnerHeadOfOrganization = false;
        if (this.$recipientMode() == Enums.RecipientMode.OwnerHeadOfOrganization) {
            toOwnerHeadOfOrganization = true;
        }

        let toOwnRole = false;
        if (this.$recipientMode() == Enums.RecipientMode.OwnRole) {
            toOwnRole = true;
        }

        Utils.updSignal(this.$configSchema, x => {
            x.taskForward.toImmediateSupervisor = toImmediateSupervisor;
            x.taskForward.toOwnerImmediateSupervisor = toOwnerImmediateSupervisor;
            x.taskForward.toOwnerHeadOfOrganization = toOwnerHeadOfOrganization;
            x.taskForward.toOwnRole = toOwnRole;
            return x;
        });

        if (this.$recipientMode() != Enums.RecipientMode.Fixed) {
            this.$configSchemaSectionEDocsLocation.set(new ConfigSchemaSectionEDocsLocation());

            Utils.updSignal(this.$configSchema, x => {
                Object.assign(x.taskForward.recipient, this.$configSchemaSectionEDocsLocation());
                return x;
            });

            this.edocsLocationRef?.clearPatriarca();
        }

        if (this.$recipientMode() != Enums.RecipientMode.Conditional) {
            this.$formFieldConditional.set(null);
            this.$valueConditional.set(null);

            Utils.updSignal(this.$configSchema, x => {
                x.taskForward.conditionalRecipients = [];
                return x;
            });

            this.edocsLocationConditionalRef?.clearPatriarca();
        }

        this._configSchemaChangeEvent.emit(this.$configSchema());
        this._recipientModeChange.emit(this.$recipientMode());
    }

    recipientNameChange(event) {
        Utils.updSignal(this.$configSchemaSectionEDocsLocation, x => {
            x.name = event;
            return x;
        });
    }

    updateSourceCode(forcedConfigSchema: ConfigSchema) {
        Utils.setSignal(this.$configSchema, forcedConfigSchema);

        Utils.setSignal(this.$configSchemaSectionEDocsLocation, Object.assign(this.$configSchemaSectionEDocsLocation(), this.$configSchema().taskForward.recipient));
        Utils.setSignal(this.$configSchemaSectionAccessLevel, Object.assign(this.$configSchemaSectionAccessLevel(), this.$configSchema().taskForward.accessLevel));

        this.initForwardInfo();
        this.accessLevelRef?.initLegalReasoningsInfo();

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    onUpdateEDocsLocation(event: ConfigSchemaSectionEDocsLocation) {
        Utils.setSignal(this.$configSchemaSectionEDocsLocation, Object.assign(this.$configSchemaSectionEDocsLocation(), event));

        Utils.updSignal(this.$configSchema, x => {
            Object.assign(x.taskForward.recipient, this.$configSchemaSectionEDocsLocation());
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    onUpdateEDocsLocationConditional(event: ConfigSchemaSectionEDocsLocation) {
        if (Utils.isNullOrEmpty(this.$formFieldConditional()) || Utils.isNullOrEmpty(this.$valueConditional())) {
            this.toastr.warning(Enums.Messages.MandatoryConditionalFields, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return;
        }

        Utils.setSignal(this.$configSchemaSectionEDocsLocationConditional, Object.assign(this.$configSchemaSectionEDocsLocationConditional(), event));

        Utils.updSignal(this.$configSchema, x => {
            x.taskForward.conditionalRecipients.push(new ConditionalRecipient({
                formFieldId: this.$formFieldConditional(),
                value: this.$valueConditional(),
                isOperationEqual: this.$isOperationEqualConditional().toString().toLowerCase() == 'true',
                recipient: Utils.clone(this.$configSchemaSectionEDocsLocationConditional())
            }));
            return x;
        });

        if (this.$configSchema().taskForward.conditionalRecipients.length == 1) {
            Utils.scrollToBottom('.scroll-wrapper');
        }

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    onUpdateAccessLevel(event: ConfigSchemaSectionAccessLevel) {
        Utils.setSignal(this.$configSchemaSectionAccessLevel, Object.assign(this.$configSchemaSectionAccessLevel(), event));

        Utils.updSignal(this.$configSchema, x => {
            Object.assign(x.taskForward.accessLevel, this.$configSchemaSectionAccessLevel());
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    // #region [===== private methods =====]
    // #endregion

    private initForwardInfo() {
        Utils.updSignal(this.$configSchema, x => {
            x.taskForward.conditionalRecipients = x.taskForward.conditionalRecipients || [];
            return x;
        });

        this.$formFieldConditionalOptions.set(this.formFlowObjectSchemaFields.map(x => ({ value: x.key, description: x.label })));

        this.$recipientMode.set(this.$configSchema()?.taskForward.toOwnRole == true
            ? Enums.RecipientMode.OwnRole
            : this.$configSchema()?.taskForward.toImmediateSupervisor == true
                ? Enums.RecipientMode.ImmediateSupervisor
                : this.$configSchema()?.taskForward.toOwnerImmediateSupervisor == true
                    ? Enums.RecipientMode.OwnerImmediateSupervisor
                    : this.$configSchema()?.taskForward.toOwnerHeadOfOrganization == true
                        ? Enums.RecipientMode.OwnerHeadOfOrganization
                        : this.$configSchema()?.taskForward.conditionalRecipients.length > 0
                            ? Enums.RecipientMode.Conditional
                            : Enums.RecipientMode.Fixed
        );

        this.recipientModeChange();
    }
}
